import { createSharedComposable } from '@vueuse/core'
import debug from '../../utils/console'

declare global {
  interface Window {
    _taboola: Record<string, unknown>[]
  }
}

export type TaboolaEventDetail = {
  baseMode: string
  container: string
  itemCount: number
  placement: string
  variant: null
}
export type TaboolaEvent = {
  isTrusted: boolean
  bubbles: boolean
  cancelBubble: boolean
  cancelable: boolean
  composed: boolean
  currentTarget: null
  defaultPrevented: boolean
  detail: TaboolaEventDetail
  eventPhase: number
  returnValue: boolean
  srcElement: null
  target: null
  timeStamp: number
  type: string
}
const console = debug.extend('useTaboolaAds')

const useTaboolaAds = createSharedComposable(() => {
  const taboolaPublisherName = useAppConfig().taboola.publisherName
  //TODO: @alexannmill provide $assertive to use instead of global
  const isHeadLoadedPromise = loadScriptAsync(
    'head',
    {
      textContent: `window._taboola = window._taboola || [];
          _taboola.push({ external_page_view: 
            ((window.performance.timing.navigationStart + window.performance.now()) * Math.random() * 1000000).toFixed() +
            "|" +
            window.location.host,
            ${getPageType()}:'auto', 
            custom_tracking:'sub_id=${window?.assertiveYieldAnalytics?.custom?.custom_1 || ''}'
          });
          !function (e, f, u, i) { if (!document.getElementById(i)){ e.async = 1; e.src = u; e.id = i; f.parentNode.insertBefore(e, f); } }
            (document.createElement('script'), document.getElementsByTagName('script')[0], '//cdn.taboola.com/libtrc/${taboolaPublisherName}/loader.js', 'tb_loader_script');
            if(window.performance && typeof(window.performance.mark) == 'function') {window.performance.mark('tbl_ic');}`,
      key: 'taboola-homepage-header-script',
    },
    {
      id: 'taboola-homepage-header-script',
      type: 'text/javascript',
      referrerpolicy: 'no-referrer-when-downgrade',
      async: true,
      onload: () => console.debug('Taboola script loaded.'),
      onerror: () => console.error('Taboola script failed to load.'),
    }
  )

  const isBodyLoadedPromise = loadScriptAsync(
    'body',
    {
      textContent: `window._taboola = window._taboola || [];
        _taboola.push({flush: true});`,
      key: 'taboola-homepage-body-script',
    },
    {
      id: 'taboola-homepage-body-script',
      type: 'text/javascript',
      async: true,
      onload: () => console.debug('Taboola body script loaded.'),
      onerror: () => console.error('Taboola body script failed to load.'),
    }
  )

  const triggerTaboolaRenderedEvent = async (
    mode: string,
    taboolaContainerName: string,
    taboolaPlacementName: string,
    callbackFn: () => void
  ): Promise<void> => {
    await isHeadLoadedPromise
    await isBodyLoadedPromise

    if (!window._taboola) {
      window._taboola = []
      console.warn('Initialized empty _taboola array after script load')
    }

    callbackFn()

    window._taboola.push({
      mode: mode,
      container: taboolaContainerName,
      placement: taboolaPlacementName,
      target_type: 'mix',
    })
  }

  const fireRenderCallback = (
    placement: string,
    executed: Ref<boolean>,
    callbackFn: () => void
  ): Promise<void> => {
    return new Promise<void>((resolve) => {
      window._taboola.push({
        listenTo: 'render',
        handler: function (e: TaboolaEvent) {
          executed.value = true
          if (e.detail.placement === placement) {
            callbackFn()
            resolve()
          }
        },
      })
    })
  }

  return {
    triggerTaboolaRenderedEvent,
    fireRenderCallback,
  }
})

export default useTaboolaAds
